import React, {ComponentProps, Fragment} from 'react';
import {cva, styles} from '../../Utilities';

function RsnTextButtonBase({
    underlined,
    children,
    decoration,
    layout,
    size,
    className,
    ...props
}: RsnTextButton.Props & {layout: 'inline' | 'block'}) {
    return (
        <button {...props} className={cls.button({layout, size, className})} type='button'>
            <span className={cls.children({underlined})}>
                {children}
                {decoration && <Fragment>&nbsp;{decorationIcons[decoration]}</Fragment>}
            </span>
        </button>
    );
}

export namespace RsnTextButton {
    export interface Props extends ComponentProps<'button'> {
        underlined?: boolean;
        size?: 'm' | 'l' | 'xl' | 'inherit';
        decoration?: keyof typeof decorationIcons;
    }

    export const Inline = (props: Props) => RsnTextButtonBase({...props, layout: 'inline'});
    Inline.displayName = 'RsnTextButton.Inline';

    export const Block = (props: Props) => RsnTextButtonBase({...props, layout: 'block'});
    Block.displayName = 'RsnTextButton.Block';
}

const cls = styles({
    button: cva(
        [
            'cursor-pointer bg-transparent',
            'text-primary-30 active:text-primary-40',
            'transition-colors duration-300 active:duration-0',
            'disabled:cursor-not-allowed disabled:text-neutral-60',
        ],
        {
            variants: {
                layout: {
                    inline: 'inline-flex',
                    block: 'flex h-12 w-full min-w-0 items-center justify-center gap-1 px-4',
                },
                size: {m: 'fv-text-m', l: 'fv-text-l', xl: 'fv-text-xl', inherit: '[font:inherit]'},
            },
            defaultVariants: {size: 'inherit'},
        }
    ),
    children: cva('inline-flex items-center whitespace-break-spaces', {
        variants: {
            underlined: {
                true: [
                    'bg-gradient-to-r from-current via-current via-[0.25rem] to-transparent to-[0.25rem]',
                    'bg-[length:0.5rem_1px] bg-[1px_bottom] bg-repeat-x',
                ],
                false: '',
            },
        },
    }),
});

const decorationIcons = {
    'arrow-right': (
        <svg className='block size-3' xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 24 24'>
            <path d='m16.172 11-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2h12.172Z' />
        </svg>
    ),
    'external-link': (
        <svg className='block size-3' xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 24 24'>
            <path d='M10 6v2H5v11h11v-5h2v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6Zm11-3v8h-2V6.413l-7.793 7.794-1.414-1.414L17.585 5H13V3h8Z' />
        </svg>
    ),
};
